import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';

const PlaceSearchAutocomplete = ({
  value,
  onChange,
  label = "City",
  required = false,
  error = false,
  helperText = "",
  placeholder = "Search for an Indian city...",
  size = "small",
  fullWidth = true,
  sx = {},
  disabled = false
}) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);


  // Set input value when value prop changes
  useEffect(() => {
    if (value && value.city) {
      setInputValue(value.city);
    } else {
      setInputValue('');
    }
  }, [value]);

  // Use OpenStreetMap Nominatim API (free, no billing required) for city search
  useEffect(() => {
    if (!inputValue.trim()) {
      setOptions([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      setLoading(true);
      
      // Use OpenStreetMap Nominatim API (free, no billing required)
      // Restrict to Indian cities only (countrycodes=in)
      const searchQuery = encodeURIComponent(inputValue);
      const url = `https://nominatim.openstreetmap.org/search?q=${searchQuery}&format=json&addressdetails=1&limit=10&featuretype=city&countrycodes=in`;
      
      fetch(url, {
        headers: {
          'User-Agent': 'SalesGro App' // Required by Nominatim
        }
      })
        .then(response => response.json())
        .then(data => {
          setLoading(false);
          
          if (data && data.length > 0) {
            // Filter to ensure only Indian cities (double-check country)
            const allCities = data
              .filter(place => {
                const address = place.address || {};
                const country = (address.country || '').toLowerCase();
                return country === 'india' || country === 'in' || place.display_name.toLowerCase().includes('india');
              })
              .map((place) => {
                const address = place.address || {};
                const city = address.city || 
                            address.town || 
                            address.village || 
                            address.municipality ||
                            place.display_name.split(',')[0];
                const state = address.state || 
                             address.region || 
                             address.province || '';
                const country = address.country || 'India';
                const pincode = address.postcode || '';
                
                // Check if city name contains non-ASCII characters (like Hindi, Devanagari script)
                // Hindi uses Devanagari script: Unicode range \u0900-\u097F
                const hasNonAscii = /[^\x00-\x7F]/.test(city);
                const hasDevanagari = /[\u0900-\u097F]/.test(city);
                
                return {
                  placeId: place.place_id.toString(),
                  city: city,
                  state: state,
                  country: country,
                  pincode: pincode,
                  fullDescription: place.display_name,
                  isEnglish: !hasNonAscii && !hasDevanagari // Flag to identify English names
                };
              });
            
            // First, filter out all non-English cities
            const englishCities = allCities.filter(place => place.isEnglish);
            
            // Then remove duplicates based on city and state name (case-insensitive)
            const uniqueCities = [];
            const seenCities = new Set();
            
            englishCities.forEach(place => {
              const cityKey = `${place.city.toLowerCase().trim()}_${place.state.toLowerCase().trim()}`;
              if (!seenCities.has(cityKey)) {
                seenCities.add(cityKey);
                uniqueCities.push(place);
              }
            });
            
            setOptions(uniqueCities);
          } else {
            setOptions([]);
          }
        })
        .catch(error => {
          console.warn('Error fetching cities from OpenStreetMap:', error);
          setLoading(false);
          setOptions([]);
        });
    }, 500); // 500ms debounce for free API

    return () => clearTimeout(timeoutId);
  }, [inputValue]);


  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
  };

  const handleChange = (event, newValue) => {
    if (!newValue) {
      setInputValue('');
      if (onChange) {
        onChange(event, null);
      }
      return;
    }

    // Handle OpenStreetMap data
    const cityData = {
      city: newValue.city || newValue,
      state: newValue.state || '',
      pincode: newValue.pincode || '',
      country: newValue.country || '',
      placeId: newValue.placeId || ''
    };
    setInputValue(cityData.city);
    if (onChange) {
      onChange(event, cityData);
    }
  };

  return (
    <Autocomplete
      fullWidth={fullWidth}
      size={size}
      options={options}
      value={value || null}
      onChange={handleChange}
      onInputChange={handleInputChange}
      inputValue={inputValue}
      loading={loading}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option.city || '';
      }}
      isOptionEqualToValue={(option, value) => {
        if (!value) return false;
        return option.placeId === value.placeId || 
               (option.city === value.city && option.state === value.state);
      }}
      filterOptions={(x) => x} // Disable built-in filtering, we handle it via API
      renderOption={(props, option) => (
        <li {...props} key={`${option.city}-${option.state}-${option.placeId}`}>
          <div>
            <div style={{ fontWeight: 500, fontSize: '14px' }}>{option.city}</div>
            {(option.state || option.country) && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                {[option.state, option.country].filter(Boolean).join(', ')}
              </div>
            )}
          </div>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          disabled={disabled}
          sx={sx}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText={
        inputValue.trim() 
          ? "No Indian cities found" 
          : "Type to search for an Indian city"
      }
    />
  );
};

export default PlaceSearchAutocomplete;

